import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Network, Wifi, WifiOff, Server, Factory, Router, AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import api from '../../utils/api';

// Format a per-spoke alert schedule (alertHours: {dow → [startMin, endMin]}, dow 0=Sun)
// into a compact "Lun–Vie 07:00–15:00 · Sáb 07:00–13:00" string, grouping consecutive
// days that share a window. Closed days are simply omitted.
function fmtAlertHours(ah, t) {
  const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  const order = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun
  const segs = [];
  let i = 0;
  while (i < order.length) {
    const w = ah[order[i]];
    if (!w) { i++; continue; }
    let j = i;
    while (j + 1 < order.length && ah[order[j + 1]] && ah[order[j + 1]][0] === w[0] && ah[order[j + 1]][1] === w[1]) j++;
    const from = t(`monitor.dayShort.${order[i]}`);
    const to = t(`monitor.dayShort.${order[j]}`);
    segs.push(`${i === j ? from : `${from}–${to}`} ${hhmm(w[0])}–${hhmm(w[1])}`);
    i = j + 1;
  }
  return segs.join(' · ');
}

// "Estado de conexión entre plantas" — a live hub-and-spoke topology of a company's sites.
// Central plant (Papelera P2) in the middle, spokes (P1, P3) on the sides, with animated links
// whose colour/flow reflect the real tunnel status (see plantConnectivityService: the link is
// measured by central-probe pings into the spoke's subnet, so it shows the WireGuard tunnel, not
// just "both have internet"). Renders on internal dashboards AND the client portal.

export const meta = {
  id: 'plant_connectivity',
  category: 'monitoring',
  titleKey: 'dashboard.widgets.plantConnectivity',
  descriptionKey: 'dashboard.widgets.plantConnectivityDesc',
  defaultSize: 'lg',
  icon: Network,
};

const C = {
  up: '#10b981', degraded: '#f59e0b', down: '#ef4444', unknown: '#f59e0b', not_configured: '#94a3b8', off_hours: '#94a3b8',
  online: '#10b981', stale: '#f59e0b', offline: '#ef4444',
};
const statusColor = (s) => C[s] || C.not_configured;

// Shared renderer so the internal widget and the portal card look identical.
export function PlantConnectivityView({ topology, t }) {
  if (!topology || !topology.plants?.length) return null;

  const central = topology.plants.find((p) => p.role === 'central') || topology.plants[0];
  const spokes = topology.plants.filter((p) => p.role !== 'central');
  // Detail strip mirrors the SVG's left-to-right layout (spokes spread, central in the middle)
  // so the box under each node is that node's plant — not the raw topology order.
  const stripPlants = [...spokes.slice(0, Math.ceil(spokes.length / 2)), central, ...spokes.slice(Math.ceil(spokes.length / 2))];

  const linkLabel = (s) => t(`plantConn.link.${s || 'not_configured'}`);
  const probeLabel = (s) => t(`plantConn.probe.${s || 'not_configured'}`);

  // Overall headline. 'unknown' (no reliable signal) counts as degraded — never as green.
  const anyDown = spokes.some((p) => p.link?.status === 'down') || central.probe?.status === 'offline';
  const anyDegraded = spokes.some((p) => ['degraded', 'unknown'].includes(p.link?.status)) || central.probe?.status === 'stale';
  const anyPending = spokes.some((p) => p.link?.status === 'not_configured');
  const overall = anyDown ? 'down' : anyDegraded ? 'degraded' : (anyPending ? 'partial' : 'up');
  const overallColor = overall === 'down' ? C.down : overall === 'degraded' ? C.degraded : overall === 'partial' ? C.degraded : C.up;

  // Geometry: central centered, spokes spread on a horizontal axis.
  const W = 620, H = 210, cy = 92;
  const cx = W / 2;
  const spokeXs = spokes.length === 1 ? [120] : spokes.map((_, i) => 100 + i * (W - 200) / Math.max(1, spokes.length - 1));

  const Node = ({ x, y, plant, isCentral }) => {
    const col = statusColor(plant.probe?.status);
    const r = isCentral ? 42 : 34;
    const Icon = isCentral ? Server : Factory;
    return (
      <g>
        {isCentral && (
          <circle cx={x} cy={y} r={r + 8} fill="none" stroke={col} strokeOpacity="0.18" strokeWidth="2">
            <animate attributeName="r" values={`${r + 4};${r + 12};${r + 4}`} dur="3s" repeatCount="indefinite" />
            <animate attributeName="stroke-opacity" values="0.28;0.05;0.28" dur="3s" repeatCount="indefinite" />
          </circle>
        )}
        <circle cx={x} cy={y} r={r} fill="var(--c-surface-2)" stroke={col} strokeWidth={isCentral ? 3 : 2} />
        <foreignObject x={x - r} y={y - r} width={r * 2} height={r * 2}>
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={isCentral ? 26 : 20} style={{ color: col }} />
          </div>
        </foreignObject>
        {/* status dot */}
        <circle cx={x + r * 0.72} cy={y - r * 0.72} r={6} fill={col} stroke="var(--c-surface)" strokeWidth="2">
          {plant.probe?.status === 'online' && (
            <animate attributeName="fill-opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
          )}
        </circle>
        {/* name + role */}
        <text x={x} y={y + r + 18} textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--c-text)">{plant.name}</text>
        <text x={x} y={y + r + 34} textAnchor="middle" fontSize="10" fill="var(--c-text-muted)">
          {isCentral ? t('plantConn.central') : probeLabel(plant.probe?.status)}
        </text>
      </g>
    );
  };

  return (
    <div>
      {/* headline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
          padding: '3px 10px', borderRadius: 999, background: `${overallColor}18`, color: overallColor,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: overallColor }} />
          {t(`plantConn.overall.${overall}`)}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--c-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={10} /> {t('plantConn.live')}
        </span>
      </div>

      {/* topology */}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <marker id="pc-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <circle cx="4" cy="4" r="2.5" fill="var(--c-border)" />
          </marker>
        </defs>
        {spokes.map((p, i) => {
          const x = spokeXs[i];
          const col = statusColor(p.link?.status);
          const up = p.link?.status === 'up';
          const pending = p.link?.status === 'not_configured' || p.link?.status === 'off_hours' || p.link?.status === 'unknown';
          const midX = (x + cx) / 2;
          // Tooltip: the actual cross-tunnel evidence (which targets, up/down) so the chip
          // is auditable — "up" must be traceable to real pings, never taken on faith.
          const targetsTip = p.link?.targetsDetail?.length
            ? p.link.targetsDetail.map((d) => `${d.status === 'up' ? '✓' : '✗'} ${d.name} (${d.ip})`).join('\n')
            : (p.link?.status === 'unknown' ? t('plantConn.unknownHint') : undefined);
          return (
            <g key={p.locationId}>
              {/* link line */}
              <line x1={x} y1={cy} x2={cx} y2={cy}
                stroke={col} strokeWidth={up ? 3 : 2}
                strokeDasharray={pending ? '4 6' : (up ? 'none' : '6 5')}
                strokeOpacity={pending ? 0.5 : 0.9} />
              {/* animated flow dots when the tunnel is healthy */}
              {up && [0, 1, 2].map((k) => (
                <circle key={k} r="3.2" fill={col}>
                  <animate attributeName="cx" values={`${x};${cx}`} dur="2.2s" begin={`${k * 0.73}s`} repeatCount="indefinite" />
                  <animate attributeName="cy" values={`${cy};${cy}`} dur="2.2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" begin={`${k * 0.73}s`} repeatCount="indefinite" />
                </circle>
              ))}
              {/* link status chip */}
              <foreignObject x={midX - 46} y={cy - 40} width="92" height="22">
                <div
                  title={p.link?.status === 'off_hours' && p.alertHours ? `${t('plantConn.alertsHint')} (${fmtAlertHours(p.alertHours, t)})` : targetsTip}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    fontSize: 10, fontWeight: 700, color: col, background: `${col}18`,
                    borderRadius: 999, padding: '2px 0', whiteSpace: 'nowrap',
                    cursor: p.link?.status === 'off_hours' ? 'help' : 'default',
                  }}
                >
                  {linkLabel(p.link?.status)}
                </div>
              </foreignObject>
              {p.link?.signal === 'tunnel-ping' && p.link.targets > 0 && (
                <text x={midX} y={cy + 24} textAnchor="middle" fontSize="9" fill="var(--c-text-muted)">
                  {p.link.reachable}/{p.link.targets} {t('plantConn.reachable')}
                </text>
              )}
              {/* WG mesh: keep the SVG clean — just a one-line indicator under the chip; the
                  full per-tunnel detail lives in the mesh bar below the diagram. */}
              {p.link?.tunnels?.length > 0 && (
                <text x={midX} y={cy + 24} textAnchor="middle" fontSize="9" fill="var(--c-text-muted)">
                  {(() => {
                    const act = p.link.tunnels.find((tn) => tn.active);
                    return act
                      ? `${t('plantConn.tunnel')} ${act.key.replace('t', '')} · ${p.link.tunnelsUp}/${p.link.tunnels.length}`
                      : `${p.link.tunnelsUp}/${p.link.tunnels.length} ${t('plantConn.paths')}`;
                  })()}
                </text>
              )}
              {/* Which WAN each side is riding — the actual path of the tunnel, leg by leg,
                  coloured by that WAN monitor's live status. */}
              {p.link?.path && (
                <foreignObject x={midX - 110} y={cy + 30} width="220" height="18">
                  <div
                    title={`${t('plantConn.pathHint')}\n${p.link.path.egress.name}: ${p.link.path.egress.ip}\n${p.link.path.ingress.name}: ${p.link.path.ingress.ip}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 9, whiteSpace: 'nowrap', cursor: 'help', color: 'var(--c-text-muted)' }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: C[p.link.path.egress.status] || C.not_configured }} />
                    <span style={{ fontWeight: 600, color: p.link.path.egress.status === 'down' ? C.down : 'var(--c-text)' }}>{p.link.path.egress.name}</span>
                    <span>→</span>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: C[p.link.path.ingress.status] || C.not_configured }} />
                    <span style={{ fontWeight: 600, color: p.link.path.ingress.status === 'down' ? C.down : 'var(--c-text)' }}>{p.link.path.ingress.name}</span>
                  </div>
                </foreignObject>
              )}
              {(p.link?.signal === 'mgmt-tunnel' || p.link?.signal === 'data-tunnel') && p.link?.status !== 'off_hours' && (
                <text x={midX} y={cy + 24} textAnchor="middle" fontSize="9" fill="var(--c-text-muted)">
                  {t(p.link?.signal === 'data-tunnel' ? 'plantConn.viaWgTunnel' : 'plantConn.viaVps')}
                </text>
              )}
              <Node x={x} y={cy} plant={p} isCentral={false} />
            </g>
          );
        })}
        <Node x={cx} y={cy} plant={central} isCentral />
      </svg>

      {/* VPN mesh bar — the per-tunnel detail, outside the SVG so nothing overlaps. One row
          per mesh-backed link (today: P1↔P2). A down tunnel here is informational, not alarm:
          the mesh exists precisely so one dead path doesn't matter. */}
      {spokes.filter((p) => p.link?.tunnels?.length > 0).map((p) => {
        const act = p.link.tunnels.find((tn) => tn.active);
        return (
          <div key={`mesh-${p.locationId}`} style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
            border: '1px solid var(--c-border)', borderRadius: 8, padding: '6px 10px',
            background: 'var(--c-surface-2)', marginTop: 6, fontSize: 11,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--c-text)' }}>
              VPN {p.name} ⟷ {central.name}
            </span>
            {act ? (
              <span style={{ color: 'var(--c-text-muted)' }}>
                {t('plantConn.activeTunnel')}: <b style={{ color: 'var(--c-text)' }}>{t('plantConn.tunnel')} {act.key.replace('t', '')}</b> · {act.legs}
              </span>
            ) : (
              <span style={{ color: 'var(--c-text-muted)' }}>{t('plantConn.meshWaiting')}</span>
            )}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {p.link.tunnels.map((tn) => (
                <span
                  key={tn.key}
                  title={`${t('plantConn.tunnel')} ${tn.key.replace('t', '')} · ${tn.legs} — ${t(`plantConn.tunnelStatus.${tn.status}`)}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: tn.active ? 'var(--c-text)' : 'var(--c-text-muted)', fontWeight: tn.active ? 700 : 500, cursor: 'help', fontSize: 10.5 }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: tn.status === 'up' ? C.up : (tn.status === 'down' ? C.down : C.not_configured),
                    boxShadow: tn.active ? `0 0 0 3px ${C.up}33` : 'none',
                  }} />
                  T{tn.key.replace('t', '')}
                </span>
              ))}
              <span style={{
                fontSize: 10.5, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
                color: p.link.tunnelsUp > 0 ? C.up : C.down,
                background: `${p.link.tunnelsUp > 0 ? C.up : C.down}18`,
              }}>
                {p.link.tunnelsUp}/{p.link.tunnels.length} {t('plantConn.paths')}
              </span>
            </span>
          </div>
        );
      })}

      {/* per-plant detail strip */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${topology.plants.length}, 1fr)`, gap: 8, marginTop: 6 }}>
        {stripPlants.map((p) => {
          const pc = statusColor(p.probe?.status);
          return (
            <div key={p.locationId} style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: '6px 8px', background: 'var(--c-surface-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, marginBottom: 3 }}>
                <Router size={12} style={{ color: pc }} /> {p.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--c-text-muted)' }}>
                {p.internet
                  ? (p.internet.status === 'up' ? <Wifi size={11} style={{ color: C.up }} /> : <WifiOff size={11} style={{ color: p.internet.status === 'down' ? C.down : C.degraded }} />)
                  : <Wifi size={11} style={{ opacity: 0.35 }} />}
                {p.devices?.total > 0
                  ? <span>{p.devices.healthy}/{p.devices.total} {t('plantConn.devices')}</span>
                  : <span style={{ opacity: 0.6 }}>{t('plantConn.noDevices')}</span>}
                {p.devices?.down > 0 && (
                  <span style={{ color: C.down, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <AlertTriangle size={10} /> {p.devices.down}
                  </span>
                )}
              </div>
              {/* Per-WAN detail: which uplink (ISP/IP) is up or down on this side. This is what
                  tells you HOW the VPN is riding — e.g. "Movistar caído, todo va por Telecentro". */}
              {p.internet?.wans?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                  {p.internet.wans.map((w) => (
                    <div key={w.ip} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9.5, color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ flexShrink: 0, width: 6, height: 6, borderRadius: '50%', background: C[w.status] || C.degraded }} />
                      <span style={{ fontWeight: 600, color: w.status === 'down' ? C.down : 'var(--c-text)' }}>{w.name}</span>
                      <span style={{ opacity: 0.75, fontFamily: 'monospace', fontSize: 9 }}>{w.ip}</span>
                      {w.status === 'down' && <span style={{ color: C.down, fontWeight: 700 }}>{t('plantConn.wanDown')}</span>}
                    </div>
                  ))}
                </div>
              )}
              {p.alertHours && (
                <div
                  title={t('plantConn.alertsHint')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'var(--c-text-muted)', marginTop: 3, cursor: 'help' }}
                >
                  <Clock size={10} /> {t('plantConn.alertsSchedule')}: {fmtAlertHours(p.alertHours, t)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PlantConnectivityWidget() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['plant-connectivity'],
    queryFn: () => api.get('/dashboard/plant-connectivity').then((r) => r.data),
    refetchInterval: 30000,
  });

  const topology = data?.topology;
  // Self-hide when the viewer has no multi-site company in scope.
  if (!isLoading && !topology) return null;

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Network size={18} style={{ color: 'var(--c-brand)' }} />
          {t('dashboard.widgets.plantConnectivity')}
          {topology?.companyName && <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 500 }}>· {topology.companyName}</span>}
        </div>
      </div>
      {isLoading
        ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>{t('common.loading')}</div>
        : <PlantConnectivityView topology={topology} t={t} />}
    </div>
  );
}
